import { IObservableArray, makeAutoObservable, observable } from "mobx";
import { AppStore } from "store/App.store";
import { AccountLeadInactivityApi } from "api/account-lead-inactivity.api";
import {
  TAccountLeadInactivity,
  TAccountLeadInactivityPayload,
} from "types/account-lead-inactivity.type";

const accountLeadInactivityApi = new AccountLeadInactivityApi();

export class AccountLeadInactivityStore {
  private readonly root: AppStore;
  private readonly accountId: number;
  private leadInactivityRules: IObservableArray<TAccountLeadInactivity>;

  constructor(root: AppStore, accountId: number) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.root = root;
    this.accountId = accountId;
    this.leadInactivityRules = observable.array<TAccountLeadInactivity>();
  }

  get leadInactivityRulesArray(): TAccountLeadInactivity[] {
    return this.leadInactivityRules.slice();
  }

  public hasRulesForOffice(officeId: number): boolean {
    const isDestination = (rule: TAccountLeadInactivity) =>
      rule.config.officeId === officeId;

    const isSourceOffice = (rule: TAccountLeadInactivity) =>
      !!rule.sourceOfficeIds?.includes(officeId);

    return this.leadInactivityRulesArray.some(
      rule => isDestination(rule) || isSourceOffice(rule)
    );
  }

  private indexOfRule(id: number) {
    return this.leadInactivityRulesArray.findIndex(rule => rule.id === id);
  }

  private upsertRule(rule: TAccountLeadInactivity) {
    const index = this.indexOfRule(rule.id);
    if (index !== -1) {
      this.leadInactivityRules[index] = rule;
    } else {
      this.leadInactivityRules.push(rule);
    }
  }

  private removeRule(id: number) {
    const index = this.indexOfRule(id);
    if (index !== -1) {
      this.leadInactivityRules.splice(index, 1);
    }
  }

  public async fetchLeadInactivity() {
    const rules = await accountLeadInactivityApi.fetchLeadInactivity(
      this.accountId
    );
    this.leadInactivityRules.replace(rules || []);
  }

  public async createLeadInactivity(payload: TAccountLeadInactivityPayload) {
    const created = await accountLeadInactivityApi.createLeadInactivity(
      this.accountId,
      payload
    );
    this.upsertRule(created);
  }

  public async updateLeadInactivity(
    leadInactivityId: number,
    payload: TAccountLeadInactivityPayload
  ) {
    const updated = await accountLeadInactivityApi.updateLeadInactivity(
      this.accountId,
      leadInactivityId,
      payload
    );
    this.upsertRule(updated);
  }

  public async deleteLeadInactivity(leadInactivityId: number) {
    await accountLeadInactivityApi.deleteLeadInactivity(
      this.accountId,
      leadInactivityId
    );
    this.removeRule(leadInactivityId);
  }
}
